import * as React from 'react';

import { CustomAvatar, FaColoredIcon } from '@europrocurement/flexy-components';
import { faTrashAlt, faPencil } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, ButtonBase } from '@mui/material';
import { Box, SxProps, useTheme } from '@mui/system';

export type AvatarCompactEditorProps = {
    canEdit?: boolean;
    avatarSrc?: string;
    firstName?: string;
    lastName?: string;
    handleAvatarChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleAvatarDeletion: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    sx?: SxProps;
};

export const AvatarCompactEditor: React.FunctionComponent<AvatarCompactEditorProps> = function ({
    canEdit = false,
    avatarSrc,
    firstName = '',
    lastName = '',
    handleAvatarChange,
    handleAvatarDeletion,
    sx,
}) {
    const { palette } = useTheme();
    const [isHovered, setIsHovered] = React.useState(false);
    const [isDeleteHovered, setIsDeleteHovered] = React.useState(false);

    if (!canEdit) {
        return (
            <Box sx={{ marginRight: '20px' }}>
                <CustomAvatar
                    firstName={firstName}
                    lastName={lastName}
                    src={avatarSrc}
                    sx={{
                        height: '108px',
                        width: '108px',
                        borderRadius: '100%',
                        lineHeight: '135px',
                        fontWeight: '700',
                        fontSize: '55px',
                        ...sx,
                    }}
                />
            </Box>
        );
    }

    return (
        <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            badgeContent={
                avatarSrc && (isHovered || isDeleteHovered) ? (
                    <Box
                        data-testid="badge"
                        onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                            event.stopPropagation();
                            handleAvatarDeletion(event);
                        }}
                        onMouseEnter={() => setIsDeleteHovered(true)}
                        onMouseLeave={() => setIsDeleteHovered(false)}
                        sx={{
                            padding: '8px',
                            borderRadius: '100%',
                            backgroundColor: isDeleteHovered
                                ? palette.error.main
                                : palette.background.paper,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'background-color 0.2s ease-in-out',
                            zIndex: 2,
                            position: 'relative',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faTrashAlt}
                            color={isDeleteHovered ? 'white' : palette.text.gris70}
                            style={{ transition: 'color 0.2s ease-in-out' }}
                        />
                    </Box>
                ) : null
            }
            sx={{ marginRight: '20px' }}
        >
            <ButtonBase
                data-testid="button-upload"
                component="label"
                role={undefined}
                tabIndex={-1}
                aria-label="Avatar image"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    borderRadius: '60px',
                    position: 'relative',
                    padding: '6px',
                    '&:has(:focus-visible)': {
                        outline: '2px solid',
                        outlineOffset: '2px',
                    },
                    backgroundColor: avatarSrc ? 'transparent' : palette.primary.light,
                }}
            >
                {avatarSrc ? (
                    <Box sx={{ position: 'relative', ...sx }}>
                        <CustomAvatar
                            firstName={firstName}
                            lastName={lastName}
                            src={avatarSrc}
                            sx={{
                                height: '108px',
                                width: '108px',
                                borderRadius: '100%',
                                lineHeight: '135px',
                                fontWeight: '700',
                                fontSize: '55px',
                                ...sx,
                            }}
                        />

                        {isHovered && !isDeleteHovered && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    borderRadius: '100%',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background-color 0.2s ease-in-out',
                                    zIndex: 1,
                                    opacity: 0,
                                    animation: 'fadeIn 0.15s ease-in-out forwards',
                                    '@keyframes fadeIn': {
                                        from: { opacity: 0 },
                                        to: { opacity: 1 },
                                    },
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faPencil}
                                    color="white"
                                    size="xl"
                                    style={{ opacity: 0.9 }}
                                />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box
                        data-testid="upload-icon"
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '108px',
                            width: '108px',
                            flexDirection: 'column',
                            gap: '8px',
                        }}
                    >
                        <FaColoredIcon
                            icon="download"
                            color={palette.primary.dark}
                        />
                        {isHovered && (
                            <Box
                                sx={{
                                    fontSize: '12px',
                                    color: palette.primary.dark,
                                    fontWeight: '500',
                                    textAlign: 'center',
                                    opacity: isHovered ? 1 : 0,
                                    transition: 'opacity 0.2s ease-in-out',
                                }}
                            >
                                Ajouter une photo
                            </Box>
                        )}
                    </Box>
                )}

                <form
                    method="post"
                    encType="multipart/form-data"
                >
                    <input
                        data-testid="input"
                        type="file"
                        accept="image/*"
                        style={{
                            border: 0,
                            clip: 'rect(0 0 0 0)',
                            height: '1px',
                            margin: '-1px',
                            overflow: 'hidden',
                            padding: 0,
                            position: 'absolute',
                            whiteSpace: 'nowrap',
                            width: '1px',
                        }}
                        onChange={handleAvatarChange}
                    />
                </form>
            </ButtonBase>
        </Badge>
    );
};
